import { CalendarDays, GraduationCap, Headset, MessagesSquare, Monitor, Wrench } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { RowMenu } from '@/components/common/RowMenu'
import { EmptyState } from '@/components/common/States'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { fmtDate, fmtMoney } from '../../shared/format'
import type { Collection, SupportRequest, WorkRecord } from '../types'
import { useCrm } from '../ui'
import { RequestThread } from './RequestThread'
import { CollectionStatusBadge, PriorityBadge, RequestStatusBadge, WorkStatusBadge } from './CrmBits'

const WORK_ICON = { demo: Monitor, installation: Wrench, training: GraduationCap }

export function CustomerLink({ id, tab }: { id: string; tab: string }) {
  const { customerName } = useCrm()
  return (
    <Link to={`/customers/${id}/${tab}`} className="text-primary font-medium hover:underline">
      {customerName(id)}
    </Link>
  )
}

export function WorkList({
  rows,
  showCustomer,
  canWrite,
  onEdit,
  onDelete,
}: {
  rows: WorkRecord[]
  showCustomer?: boolean
  canWrite: boolean
  onEdit: (w: WorkRecord) => void
  onDelete: (w: WorkRecord) => void
}) {
  const { t } = useTranslation()
  const { staffName } = useCrm()
  if (!rows.length) return <EmptyState icon={Monitor} title={t('crm.work.empty')} />
  return (
    <ul className="divide-y">
      {rows.map((w) => {
        const Icon = WORK_ICON[w.kind]
        return (
          <li key={w.id} className="flex flex-wrap items-start gap-x-3 gap-y-2 px-4 py-3">
            <span className="bg-primary/10 text-primary flex size-9 shrink-0 items-center justify-center rounded-lg">
              <Icon className="size-4" aria-hidden />
            </span>
            <div className="min-w-0 flex-1 basis-56">
              <p className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                {t(`crm.workKind.${w.kind}`)}
                {showCustomer ? <CustomerLink id={w.customerId} tab="delivery" /> : null}
              </p>
              <p className="text-muted-foreground text-xs">
                <CalendarDays className="mr-1 inline size-3" aria-hidden />
                {fmtDate(w.date)} · {staffName(w.staffId)}
              </p>
              {w.note ? <p className="text-muted-foreground mt-1 text-sm">{w.note}</p> : null}
            </div>
            <WorkStatusBadge status={w.status} />
            {canWrite ? (
              <RowMenu label={t(`crm.workKind.${w.kind}`)} onEdit={() => onEdit(w)} onDelete={() => onDelete(w)} />
            ) : null}
          </li>
        )
      })}
    </ul>
  )
}

export function RequestList({
  rows,
  showCustomer,
  canWrite,
  onEdit,
  onDelete,
}: {
  rows: SupportRequest[]
  showCustomer?: boolean
  canWrite: boolean
  onEdit: (r: SupportRequest) => void
  onDelete: (r: SupportRequest) => void
}) {
  const { t } = useTranslation()
  const { staffName } = useCrm()
  const [openId, setOpenId] = useState<string | null>(null)
  if (!rows.length) return <EmptyState icon={Headset} title={t('crm.request.empty')} />
  return (
    <>
      <ul className="divide-y">
        {rows.map((r) => (
          <li key={r.id} className="flex flex-wrap items-start gap-x-3 gap-y-2 px-4 py-3">
            <div className="min-w-0 flex-1 basis-56">
              <button
                type="button"
                onClick={() => setOpenId(r.id)}
                className="hover:text-primary text-left text-sm font-semibold focus-visible:underline focus-visible:outline-none"
              >
                <span className="text-muted-foreground mr-2 font-mono text-xs">{r.number}</span>
                {r.title}
              </button>
              <p className="text-muted-foreground text-xs">
                {showCustomer ? (
                  <>
                    <CustomerLink id={r.customerId} tab="requests" /> ·{' '}
                  </>
                ) : null}
                {fmtDate(r.createdAt.slice(0, 10))} · {r.assigneeId ? staffName(r.assigneeId) : t('crm.unassigned')}
                {r.module ? ` · ${r.module}` : ''}
              </p>
              {r.description ? (
                <p className="text-muted-foreground mt-1 line-clamp-2 text-sm">{r.description}</p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => setOpenId(r.id)}
              className="text-muted-foreground hover:bg-muted hover:text-foreground inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs"
              aria-label={t('crm.request.openThread', { count: r.messages.length })}
            >
              <MessagesSquare className="size-3.5" aria-hidden />
              <span className="tabular-nums">{r.messages.length}</span>
            </button>
            <PriorityBadge priority={r.priority} />
            <RequestStatusBadge status={r.status} />
            {canWrite ? <RowMenu label={r.number} onEdit={() => onEdit(r)} onDelete={() => onDelete(r)} /> : null}
          </li>
        ))}
      </ul>
      {openId ? <RequestThread requestId={openId} canWrite={canWrite} onClose={() => setOpenId(null)} /> : null}
    </>
  )
}

export function CollectionsList({
  rows,
  showCustomer,
  canPay,
  onReceipt,
}: {
  rows: Collection[]
  showCustomer?: boolean
  canPay: boolean
  onReceipt: (c: Collection) => void
}) {
  const { t } = useTranslation()
  if (!rows.length) return <EmptyState icon={CalendarDays} title={t('crm.collection.empty')} />
  return (
    <ul className="divide-y">
      {rows.map((c) => (
        <li key={c.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
          <div className="min-w-0 flex-1 basis-56">
            <p className="text-sm font-semibold">
              <span className="text-muted-foreground mr-2 font-mono text-xs">{c.code}</span>
              {c.description}
            </p>
            <p className={cn('text-xs', c.status === 'overdue' ? 'text-destructive' : 'text-muted-foreground')}>
              {showCustomer ? (
                <>
                  <CustomerLink id={c.customerId} tab="finance" /> ·{' '}
                </>
              ) : null}
              {t('crm.collection.due', { date: fmtDate(c.dueDate) })}
            </p>
          </div>
          <div className="text-right text-sm">
            <p className="font-semibold tabular-nums">{fmtMoney(c.amount)}</p>
            <p className="text-muted-foreground text-xs">
              {t('crm.collection.paidShort')} <span className="tabular-nums">{fmtMoney(c.paid)}</span>
            </p>
          </div>
          <CollectionStatusBadge status={c.status} />
          {canPay && c.status !== 'paid' ? (
            <Button size="sm" variant="outline" onClick={() => onReceipt(c)}>
              {t('crm.receipt.add')}
            </Button>
          ) : (
            <span className="hidden w-[6.5rem] sm:block" aria-hidden />
          )}
        </li>
      ))}
    </ul>
  )
}

export function StaffChip({ id, role }: { id: string; role?: string }) {
  const { staffName, staffTitle } = useCrm()
  return (
    <span className="flex min-w-0 items-center gap-2">
      <Avatar name={staffName(id)} className="size-8" />
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">{staffName(id)}</span>
        <span className="text-muted-foreground block truncate text-xs">{role ?? staffTitle(id)}</span>
      </span>
    </span>
  )
}
